/**
 * @class
 */
import Base from '@netx/core/lib/netx-model';

import AssetModel from '@netx/core-asset/models/asset';
import CartAssetsCollection from '@netx/core-cart/collections/cartAssets';
import CategoryModel from '@netx/core-category/models/category';
import CategoryAssetsCollection from '@netx/core-category/collections/categoryAssets';
import CategoriesCollection from '@netx/core-category/collections/categories';

export default class CMSBlock extends Base {
	/**
	 * @type {Netx.Rpc}
	 * @private
	 */
	get rpc() {
		return true;
	}

	/**
	 * @property {object}  defaults - The default attribute values
	 */
	get defaults() {
		return {
			// id: 0, // DO NOT default the idAttribute.  .isNew() will give false negatives if you do.
			/** @property {number}  defaults.userId		- Creator/owner of the page */
			userId: 0,
			/** @property {string}  defaults.modDate		- Set by server on successful update */
			modDate: '',
			/** @property {array} 	defaults.assets 			-- server-side array of asset ids for the assets in this block */
			assetIds: [],
			/** @property {number}  defaults.pageId				- id of parent page */
			pageId: 0,
			/** @property {string}  defaults.type				- type of block (html, asset, category, carousel, etc -- see cms/properties/cms#blocks.types */
			type: '',

			// block display options
			/** @property {boolean} defaults.buttonDownload */
			buttonDownload: false,
			/** @property {boolean} defaults.buttonCart */
			buttonCart: false,
			/** @property {string} defaults.linkUrl */
			linkUrl: '',
			/** @property {string} defaults.linkTitle */
			linkTitle: '',
			/** @property {boolean} defaults.displayAssetName */
			displayAssetName: '0',
			/** @property {string} defaults.imageSize */
			imageSize: 'thumb',
			/** @property {string} defaults.imageSize */
			itemImageSize: 'thumb',
			/** @property {string} defaults.listTitle */
			listTitle: '',
			/** @property {boolean} defaults.listButtonDownload */
			listButtonDownload: false,
			/** @property {boolean} defaults.listButtonCart */
			listButtonCart: false,
			/** @property {boolean} defaults.displayListItemName */
			displayListItemName: false,

			/** @property {string}  defaults.html					- text content relevant to the block type (if any) */
			html: '',
			/** @property {array}  	defaults.contentIds		- object IDs relevant to the type (if any) */
			contentIds: [],
			/** @property {array}  	defaults.contentLabels		- labels of objects relevant to the type (if any) */
			contentLabels: [],
		};
	}

	/**
	 * list of attributes to convert to pack into goo instead of transmitting directly:
	 * @see packAttrs
	 * @see unpackAttrs
	 * @property {array}
	 */
	get gooAttrs() {
		return [
			'type',
			'html',
			'contentIds',
			'contentLabels',
			'buttonDownload',
			'buttonCart',
			'linkUrl',
			'linkTitle',
			'imageSize',
			'itemImageSize',
			'displayAssetName',
			'listTitle',
			'listButtonDownload',
			'listButtonCart',
			'displayListItemName',
			'ordinal',
		];
	}

	get clientAttrs() {
		return ['assetModel', 'cartCollection', 'categoryCollection'];
	}

	/**
	 * When syncing, pass an array of assetIds as our 'assetIds' property
	 */
	self() {
		var me = super.self(); // returns without any 'assets' property, because 'assets' is in clientAttrs
		//me.assetIds = this.get('assets') ? this.get('assets').ids() : [];
		return me;
	}

	/**
	 * RPC methods:
	 * @rpcs
	 */
	get methods() {
		return {
			/**
			 * NOTE: Backbone renames the CRUD methods (create/read/update/delete) to fetch/save/destroy.
			 * These @method comments straighten that out in the auto-generated docs.
			 * @method fetch
			 */
			read: [
				'getBlocks',
				function () {
					return [this.id];
				},
			],
			/** @method save */
			create: [
				'setBlock',
				function () {
					return this.self();
				},
			],
			update: [
				'setBlock',
				function () {
					return this.self();
				},
			],
			/** @method destroy */
			remove: ['deleteBlock', 'id'],
		};
	}

	/**
	 * backbone parser for read, update & create methods.
	 * @param {object} result server response
	 * @param {object} opts backbone options
	 * @returns {object} the parsed attribute data
	 */
	parse(result, opts) {
		if (_.isNumeric(result)) {
			// result of setBlock(): an object ID
			this.set({id: result}, opts);
		} else {
			// result of getBlocks(id): a one-object array or
			// an attributes object:
			this._parse(_.isArray(result) ? result[0] : result, opts);
		}

		return this.attributes;
	}

	// TODO abstract this whole attrs->objects thing better.
	/** override */
	set(attrs, opts = {}) {
		return opts.parse ? this._parse(attrs, opts) : super.set(attrs, opts);
	}

	/**
	 * _parse decomposes and applies the server data for a block,
	 * managing the unpacking of 'goo' and the instantiation of the assets collection --
	 * either from an 'assets' array/collection containing data (the client's representation),
	 * or an 'assetIds' array  containing ids (as stored on the server).
	 *
	 * @param {object} attrs unparsed attributes
	 * @param {object} opts backbone options
	 */
	_parse(attrs, opts) {
		var assetModel,
			cartCollection,
			categoryCollection,
			cId,
			attributes = this.unpackAttrs(attrs);
		this.set(attributes, {parse: false});

		// only parse the assets of blocks that have assets
		switch (attributes.type) {
			case 'asset':
				cId = this.get('contentIds') ? this.get('contentIds')[0] : undefined;
				assetModel = new AssetModel();
				if (cId) {
					assetModel.set({assetId: cId}, {parse: false});
					assetModel.fetch().then(function () {
						// assetPreviewView doesn't wait for the model to fetch
						this.trigger('change:assetId', this, this.get('assetId'));
					});
				}
				this.set({assetModel: assetModel}, {parse: false});

				break;
			case 'category':
				cId = this.get('contentIds') ? this.get('contentIds')[0] : undefined;

				if (cId && cId != this.get('categoryId')) {
					this.set({categoryId: cId}, {parse: false});

					categoryCollection = new CategoriesCollection([], {
						currentCat: new CategoryModel({categoryid: cId}),
						pagerRoutes: false,
					});
					categoryCollection.pageSize = categoryCollection.chunkSize;
					categoryCollection.fetch();
					this.categoryCollection = categoryCollection;
				}
				if (!cId) {
					categoryCollection = new CategoryAssetsCollection([], {
						pagerRoutes: false,
					});
					categoryCollection.pageSize = categoryCollection.chunkSize;
					this.categoryCollection = categoryCollection;
				}

				break;

			case 'carousel':
			case 'categoryAssetList':
				//this.set(attributes, { parse: false });
				cId = this.get('contentIds') ? this.get('contentIds')[0] : undefined;

				if (cId && cId != this.get('categoryId')) {
					this.set({categoryId: cId}, {parse: false});

					categoryCollection = new CategoryAssetsCollection([], {
						pagerRoutes: false,
					});
					categoryCollection.pageSize = categoryCollection.chunkSize = 30;
					categoryCollection.getCategoryAssets(parseInt(cId));

					// this will pass the block options to the listItemViews and update as the block updates
					categoryCollection.block = this;

					this.categoryCollection = categoryCollection;

					// DAM-15696 - Relying on content labels stored in the goo means we have out
					// of date information.  Fetching to get the actual path of the category
					let category = new CategoryModel({categoryid: cId});
					category.fetch().then((cat) => {
						this.set({contentLabels: [cat.repositorydirectory]});
					});
				}

				if (!cId) {
					categoryCollection = new CategoryAssetsCollection([]);
					categoryCollection.pageSize = categoryCollection.chunkSize;
					this.categoryCollection = categoryCollection;
				}

				break;

			case 'savedCart':
				//this.set(attributes, { parse: false });
				cId = this.get('contentIds') ? this.get('contentIds')[0] : undefined;

				if (cId && cId != this.get('cartId')) {
					this.set({cartId: cId}, {parse: false});
					cartCollection = new CartAssetsCollection([], {pagerRoutes: false});
					cartCollection.pageSize = cartCollection.chunkSize = 30;
					cartCollection.fetchSavedCart(parseInt(cId));
					cartCollection.block = this;
					this.cartCollection = cartCollection;
				}

				if (!cId) {
					cartCollection = new CartAssetsCollection([], {pagerRoutes: false});
					cartCollection.pageSize = cartCollection.chunkSize;
					this.cartCollection = cartCollection;
				}

				break;

			//case 'html':
			default:
				attributes.assetIds = [];
				break;
		}
	}

	/** @method */
	setType(newType) {
		if (this.get('type') === newType) return;

		const attrs = _.extend(this.defaults, {type: newType});
		this.set(attrs, {parse: true});
	}

	/** @type {string} */
	static get __name__() {
		return 'CMSBlock';
	}
}
